import {cn} from "@/lib/utils"
import * as React from "react"
import {useState} from "react"
import {LucideIcon} from "lucide-react"
import {Button} from "@/components/ui/button"
import {Tooltip, TooltipContent, TooltipTrigger} from "@/components/ui/tooltip"
import {MetaHandler} from "@/main/common/MetaHandler"
import {LocalizationUtil} from "@/main/util/LocalizationUtil"
import {
    type ContextualAccountAccessDialogContext,
    resolveContextualAccountAccessDialogOptions,
} from "@/main/react/account_access/AccountAccessDialogContext"
import {openAccountAccessDialog} from "@/main/react/account_access/AccountAccessDisplayerDialog"
import {CtaButtonContent} from "./CtaButtonContent"
import {
    CtaCountFeedback,
    CtaInteractionEffect,
    CtaSize,
    CtaVariant,
    getBaseButtonVariant,
    getCtaClasses,
    getCtaIconBaseClass,
    resolveActiveClass,
    warnIfCtaHasNoAccessibleName,
} from "./ctaShared"
import {useCtaInteractionFeedback} from "./useCtaInteractionFeedback"

type CtaButtonBaseProps = {
    icon: LucideIcon
    text?: string | null
    /**
     * The accessible name for an ICON-ONLY CTA (`text` null). Required in practice: the glyph is
     * `aria-hidden` and both reserve snapshots are too, so without this the button announces as a
     * bare "button" — which is what shipped on four `…` overflow menus and the group notification
     * bell (`DASHBOARD-1` / `GROUP-6` / `DISCUSSION-13`). Pass `localizer(<existing key>)`: minting
     * copy for a label is a 24-locale bill (AGENTS.md rule 11). `aria-labelledby` through `rest` is
     * the other sanctioned channel when the name can be composed from text already on the page.
     * Enforced by `ctaAccessibleName.contract.test.tsx`.
     */
    ariaLabel?: string | null
    buttonCount?: number | null
    type?: "button" | "submit" | "reset"
    disabled?: boolean
    extraClass?: string
    buttonActive?: boolean
    activeClass?: string
    buttonData?: string | null
    onClick?: React.ButtonHTMLAttributes<HTMLButtonElement>["onClick"]
    href?: string | null
    roundedLeft?: boolean
    roundedRight?: boolean
    displayTextOnMobile?: boolean
    requiresLogin?: boolean
    disabledReason?: string | null
    /**
     * A11 (`ACCOUNT-ACCESS-31` / `-50`): "not ready yet" WITHOUT the `disabled` attribute. The control
     * stays in tab order and answers a tap or the keyboard's Go key, so the form's own submit handler
     * runs its validation and says what is missing; it only STATES unavailability (`aria-disabled`,
     * the disabled tier's muted look). A really-disabled submit was skipped by Tab, silent to a tap,
     * and swallowed Enter, so the member never learned why. Use it only where the press validates.
     */
    softDisabled?: boolean
    loadingText?: string | null
    loadingReason?: string | null
    size?: CtaSize
    variant?: CtaVariant
    playwrightId?: string
    suppressTooltip?: boolean
    tooltip?: string | null
    isLoading?: boolean
    interactionEffect?: CtaInteractionEffect
    countFeedback?: CtaCountFeedback
}

type CtaButtonLoginGateProps =
    | {
        requiresLogin?: false
        accountAccessDialog?: never
    }
    | {
        requiresLogin: true
        accountAccessDialog: ContextualAccountAccessDialogContext
    }

export type CtaButtonProps = CtaButtonBaseProps & CtaButtonLoginGateProps

export type CtaButtonComponentProps = CtaButtonProps & Omit<React.ComponentProps<typeof Button>, "size" | "isLoading" | "variant">

const SOFT_DISABLED_CLASSES = "aria-disabled:text-muted-foreground aria-disabled:bg-muted aria-disabled:border-border aria-disabled:shadow-none aria-disabled:hover:bg-muted aria-disabled:hover:text-muted-foreground"

export const CtaButton = React.forwardRef<HTMLButtonElement, CtaButtonComponentProps>(function CtaButton(
    {
        icon: Icon,
        text = null,
        ariaLabel = null,
        buttonCount = null,
        type = "button",
        disabled = false,
        extraClass = "",
        buttonActive = false,
        activeClass,
        buttonData = null,
        onClick,
        href = null,
        roundedLeft = false,
        roundedRight = false,
        displayTextOnMobile = true,
        title,
        requiresLogin = false,
        accountAccessDialog,
        disabledReason = null,
        softDisabled = false,
        loadingText = null,
        loadingReason = null,
        size = "md",
        variant = "ghost",
        playwrightId = undefined,
        suppressTooltip = false,
        tooltip = null,
        isLoading: externalIsLoading,
        interactionEffect,
        countFeedback = "none",
        ...rest
    },
    ref,
) {
    const [internalIsLoading, setInternalIsLoading] = useState(false)

    const effectiveIsLoading = typeof externalIsLoading === "boolean" ? externalIsLoading : internalIsLoading
    const resolvedActiveClass = resolveActiveClass(buttonActive, variant, activeClass, extraClass)
    const classes = getCtaClasses({
        size,
        variant,
        roundedLeft,
        roundedRight,
        resolvedActiveClass,
        extraClass,
    })
    const effectiveDisabled = disabled || effectiveIsLoading
    const effectiveTooltip = disabledReason ?? (effectiveIsLoading ? (loadingReason ?? undefined) : (tooltip ?? undefined))
    const effectiveText = effectiveIsLoading ? (loadingText ?? text) : text
    // `aria-label` may also arrive through `rest` (it is a valid DOM prop and predates `ariaLabel`),
    // so both channels are read here and both satisfy the invariant below.
    const restAriaProps = rest as Record<string, unknown>
    const effectiveAriaLabel = ariaLabel ?? (restAriaProps["aria-label"] as string | undefined) ?? undefined
    warnIfCtaHasNoAccessibleName("CtaButton", {
        text: effectiveText,
        ariaLabel: effectiveAriaLabel,
        title,
        ariaLabelledBy: restAriaProps["aria-labelledby"],
    })
    const effectiveIconBase = getCtaIconBaseClass(size)
    const effectiveInteractionEffect = interactionEffect ?? (variant === "link" ? "none" : "weight")
    const {rootInteractionProps, armInteraction, blurAfterPointerPress} = useCtaInteractionFeedback({
        interactionEffect: effectiveInteractionEffect,
        countFeedback,
        active: buttonActive,
        count: buttonCount,
    })

    const handleClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
        if (effectiveDisabled) {
            try {
                e?.preventDefault()
                e?.stopPropagation()
            } catch {
                // ignore
            }
            return
        }

        blurAfterPointerPress(e.currentTarget)

        if (requiresLogin && !MetaHandler.isUserLoggedIn()) {
            try {
                e?.preventDefault()
                e?.stopPropagation()
            } catch {
                // ignore
            }
            openAccountAccessDialog(LocalizationUtil.get, resolveContextualAccountAccessDialogOptions(LocalizationUtil.get, accountAccessDialog!))
            return
        }

        if (onClick) {
            try {
                armInteraction()
                const result = (onClick as any)(e as any)
                if (result && typeof (result as any).then === "function") {
                    if (typeof externalIsLoading !== "boolean") setInternalIsLoading(true)
                    try {
                        await result
                    } finally {
                        if (typeof externalIsLoading !== "boolean") setInternalIsLoading(false)
                    }
                }
            } catch (err) {
                if (typeof externalIsLoading !== "boolean") setInternalIsLoading(false)
                throw err
            }
        }
    }

    if (href) {
        const anchorOnClick = onClick as React.ComponentProps<"a">["onClick"] | undefined

        const handleAnchorClick: React.ComponentProps<"a">["onClick"] = async (e) => {
            if (effectiveDisabled) {
                try {
                    e?.preventDefault()
                } catch {
                    // ignore
                }
                return
            }

            if (requiresLogin && !MetaHandler.isUserLoggedIn()) {
                try {
                    e?.preventDefault()
                } catch {
                    // ignore
                }
                openAccountAccessDialog(LocalizationUtil.get, resolveContextualAccountAccessDialogOptions(LocalizationUtil.get, accountAccessDialog!))
                return
            }

            if (anchorOnClick) {
                try {
                    armInteraction()
                    const result = (anchorOnClick as any)(e as any)
                    if (result && typeof (result as any).then === "function") {
                        if (typeof externalIsLoading !== "boolean") setInternalIsLoading(true)
                        try {
                            await result
                        } finally {
                            if (typeof externalIsLoading !== "boolean") setInternalIsLoading(false)
                        }
                    }
                } catch (err) {
                    if (typeof externalIsLoading !== "boolean") setInternalIsLoading(false)
                    throw err
                }
            }
        }

        const anchorButton = (
            <Button asChild ref={ref} className={classes} variant={getBaseButtonVariant(variant)} size={size as any} {...(rest as React.ComponentProps<typeof Button>)}>
                <a
                    href={href}
                    aria-disabled={effectiveDisabled}
                    onClick={handleAnchorClick}
                    aria-label={effectiveAriaLabel}
                    title={title ?? (effectiveText != null ? String(effectiveText) : undefined)}
                    data-playwright-id={playwrightId}
                    data-variable={buttonData ?? undefined}
                    {...rootInteractionProps}
                    {...(rest as React.ComponentProps<"a">)}
                >
                    <CtaButtonContent
                        icon={Icon}
                        text={text}
                        loadingText={loadingText}
                        buttonCount={buttonCount}
                        isLoading={effectiveIsLoading}
                        displayTextOnMobile={displayTextOnMobile}
                        resolvedActiveClass={resolvedActiveClass}
                        iconBaseClass={effectiveIconBase}
                    />
                </a>
            </Button>
        )

        if (effectiveTooltip && !suppressTooltip) {
            return (
                <Tooltip>
                    <TooltipTrigger asChild>{anchorButton}</TooltipTrigger>
                    <TooltipContent side="top">{effectiveTooltip}</TooltipContent>
                </Tooltip>
            )
        }

        return anchorButton
    }

    // `softDisabled` borrows the disabled tier's look through `aria-disabled:` variants, because the
    // base's `disabled:` variants only match the real attribute this mode deliberately omits.
    const softDisabledActive = softDisabled && !effectiveDisabled
    const buttonElement = (
        <Button
            ref={ref}
            type={type}
            disabled={effectiveDisabled}
            aria-disabled={softDisabledActive ? true : undefined}
            className={softDisabledActive ? cn(classes, SOFT_DISABLED_CLASSES) : classes}
            variant={getBaseButtonVariant(variant)}
            size={size as any}
            onClick={handleClick}
            aria-label={effectiveAriaLabel}
            title={title ?? (effectiveText != null ? String(effectiveText) : undefined)}
            data-playwright-id={playwrightId}
            data-variable={buttonData ?? undefined}
            {...rootInteractionProps}
            {...rest}
        >
            <CtaButtonContent
                icon={Icon}
                text={text}
                loadingText={loadingText}
                buttonCount={buttonCount}
                isLoading={effectiveIsLoading}
                displayTextOnMobile={displayTextOnMobile}
                resolvedActiveClass={resolvedActiveClass}
                iconBaseClass={effectiveIconBase}
            />
        </Button>
    )

    if (effectiveTooltip && !suppressTooltip) {
        return (
            <Tooltip>
                <TooltipTrigger asChild>
                    <span className="inline-block">{buttonElement}</span>
                </TooltipTrigger>
                <TooltipContent side="top">{effectiveTooltip}</TooltipContent>
            </Tooltip>
        )
    }

    return buttonElement
})

CtaButton.displayName = "CtaButton"
